/*
 * ---------------------------------------------------------------------------
 * COPIE EMBARQUÉE DU SDK OFFICIEL — ne pas modifier à la main.
 *
 * Source : sdk/src/index.ts, à la racine du dépôt de la plateforme.
 *
 * Pourquoi une copie ? Pour que ce dossier soit **réellement autonome** : un
 * développeur peut le récupérer seul, sans le reste du dépôt, faire
 * `npm install` et le lancer. C'est aussi ce qui permet à son image Docker de
 * se construire avec ce dossier pour contexte.
 *
 * Dans VOTRE projet, vous n'aurez pas ce fichier : vous installerez le paquet
 * publié (`npm install @dash-lite/sdk`) et les imports fonctionneront tels
 * quels — l'alias `@dash-lite/sdk` de `vite.config.ts` pointe ici et n'aura
 * plus lieu d'être.
 *
 * Pour resynchroniser cette copie depuis le dépôt de la plateforme :
 *
 *     npm run sdk:sync
 * ---------------------------------------------------------------------------
 */

/*
 * SDK TypeScript officiel de l'API Dash Lite — /api/v1 (CDC §5.2).
 *
 * Léger par conception : des types alignés sur la spécification OpenAPI
 * et un client fetch, sans aucune dépendance. L'environnement (test /
 * production) est porté par la clé de site, jamais par l'URL : passer de
 * dev à prod = changer une variable d'environnement.
 */

// ---------------------------------------------------------------------------
// Catalogue des codes d'erreur (CDC §5.1) — le front branche sa logique sur
// error.code, jamais sur les messages.
// ---------------------------------------------------------------------------

export type DashLiteErrorCode =
    | 'invalid_site_key'
    | 'environment_mismatch'
    | 'site_suspended'
    | 'unauthenticated'
    | 'invalid_credentials'
    | 'forbidden'
    | 'entitlement_required'
    | 'validation_failed'
    | 'not_found'
    | 'company_not_found'
    | 'upstream_unavailable'
    | 'rate_limited'
    | 'offer_not_available'
    | 'payments_not_configured'
    | 'invalid_redirect_url'
    | 'invalid_reset_token'
    | 'payment_flagged_fraud'
    | 'account_exists'
    | 'subscription_not_found'
    | 'identification_ambiguous'
    | 'server_error';

export class DashLiteApiError extends Error {
    constructor(
        public readonly code: DashLiteErrorCode,
        message: string,
        public readonly status: number,
        public readonly details: Record<string, unknown> = {},
    ) {
        super(message);
        this.name = 'DashLiteApiError';
    }
}

// ---------------------------------------------------------------------------
// Types des ressources (schéma normalisé, CDC §6.3 ; offres, CDC §5.5)
// ---------------------------------------------------------------------------

export type CompanySearchResult = {
    name: string;
    registration_number: string;
    country: string;
    city: string | null;
    legal_form: string | null;
};

/**
 * Fiche entreprise en trois blocs : le **noyau** (identique pour tous les
 * pays), **`registry`** (ce que le registre national expose, sous des clés
 * communes) et **`derived`** (les valeurs calculées par la plateforme). Les
 * clés d'un pays qui ne fournit pas une information valent `null` — elles ne
 * disparaissent jamais.
 */
export type Company = {
    name: string;
    registration_number: string;
    country: string;
    legal_form: string | null;
    status: 'active' | 'ceased' | 'unknown';
    incorporation_date: string | null;
    address: {
        line1: string | null;
        postal_code: string | null;
        city: string | null;
        country: string;
    };
    officers: CompanyOfficer[];
    vat_number: string | null;
    registry: CompanyRegistry;
    derived: CompanyDerived;
    provider_data: { provider: string; raw: Record<string, unknown> };
};

export type CompanyOfficer = {
    role: string | null;
    name: string;
    /** Détails du registre, quand le pays les fournit. */
    is_legal_entity?: boolean;
    appointed_at?: string | null;
    birth_date?: string | null;
    birth_place?: string | null;
    birth_country?: string | null;
    nationality?: string | null;
};

/** Données du registre national, sous des clés communes à tous les pays. */
export type CompanyRegistry = {
    registration_number_formatted: string | null;
    establishment_number: string | null;
    establishment_number_formatted: string | null;
    registration_id: string | null;
    registration_status: string | null;
    registration_date: string | null;
    first_registration_date: string | null;
    striking_off_date: string | null;
    management_number: string | null;
    court: { name: string | null; code: string | null };
    duration_years: number | null;
    cessation_date: string | null;
    reopening_date: string | null;
    activity_start_date: string | null;
    legal_category_code: string | null;
    trade_name: string | null;
    acronym: string | null;
    sign: string | null;
    is_legal_entity: boolean;
    capital: {
        amount: number | null;
        currency: string | null;
        variable: boolean;
        formatted: string | null;
    };
    activity: {
        code: string | null;
        label: string | null;
        description: string | null;
        domain: string | null;
    };
    fiscal_year_end: string | null;
    next_fiscal_year_end: string | null;
    workforce: {
        count: number | null;
        range: string | null;
        year: number | null;
        is_employer: boolean;
    };
    headquarters: {
        establishment_number: string | null;
        line1: string | null;
        line2: string | null;
        postal_code: string | null;
        city: string | null;
        country: string | null;
        sign: string | null;
        domiciliation: string | null;
    };
    establishments_count: number | null;
    open_establishments_count: number | null;
    collective_proceedings: { exists: boolean; ongoing: boolean };
    flags: {
        mission_led: boolean;
        social_economy: boolean;
        sole_shareholder: boolean;
        publishable: boolean;
    };
    last_updated_at: string | null;
};

/**
 * Valeurs calculées par la plateforme : elles ne figurent pas telles quelles
 * dans les registres, et sont produites au même endroit pour tous les sites.
 * `null` quand le calcul n'est pas possible — rien n'est inventé.
 */
export type CompanyDerived = {
    /** Identifiant européen unique (EUID). */
    european_identifier: string | null;
    /** Numéro de gestion, reconstitué si le registre ne le fournit pas. */
    management_number: string | null;
    /** Juridiction compétente, avec son adresse postale résolue. */
    court: {
        name: string;
        label: string;
        address: {
            line1: string | null;
            postal_code: string | null;
            city: string | null;
            country: string;
        };
    } | null;
    /** Fin de la personne morale (immatriculation + durée statutaire). */
    entity_end_date: string | null;
};

/**
 * Configuration publique du site : de quoi afficher vos pages sans multiplier
 * les appels. Réponse stable, mise en cache par le navigateur.
 */
export type SiteConfig = {
    name: string;
    country: string;
    /** L'environnement porté par votre clé, sans avoir à lire son préfixe. */
    environment: 'test' | 'live';
    contact_email: string | null;
    search_requires_auth: boolean;
    offer: Offer | null;
};

export type Offer = {
    id: number;
    name: string;
    description: string | null;
    type: 'one_time' | 'subscription' | 'trial_then_subscription';
    price: { amount: number; currency: string };
    billing_interval: 'month' | 'year' | null;
    trial: { price: { amount: number; currency: string }; days: number | null } | null;
    access_duration_hours: number | null;
};

export type Entitlement = {
    status: 'active' | 'none' | 'past_due' | 'canceled' | 'expired';
    /** Le seul champ à consulter pour ouvrir l'accès. */
    has_access: boolean;
    starts_at: string | null;
    /** Fin de l'accès. */
    ends_at: string | null;
    /** Prochain prélèvement d'un abonnement en cours — `null` s'il est résilié. */
    renews_at: string | null;
    offer: {
        name: string;
        type: Offer['type'];
        price: { amount: number; currency: string };
    } | null;
    /** De quoi afficher « Visa •••• 4242 ». */
    card: { brand: string | null; last4: string } | null;
    /** Le contexte que vous aviez attaché à l'achat. */
    metadata: Record<string, string> | null;
};

/**
 * Identification d'un client qui résilie sans compte : les 4 derniers
 * chiffres de sa carte, plus son email **ou** la date du prélèvement.
 */
export type SubscriptionIdentification = {
    card_last4: string;
} & ({ email: string; payment_date?: never } | { payment_date: string; email?: never });

export type SubscriptionLookup = {
    /** Masqué : confirme le compte sans divulguer l'adresse. */
    email: string;
    card: { brand: string | null; last4: string | null };
    offer: string | null;
    entitlement: Entitlement;
};

export type Me = {
    id: number;
    email: string;
    created_at: string | null;
    entitlement: Entitlement;
};

export type AuthResult = {
    token: string;
    user: {
        id: number;
        email: string;
        created_at: string | null;
        entitlement: Entitlement;
    };
};

/**
 * Réponse de `POST /payment` : le `client_secret` à confirmer côté client et
 * la **clé publishable Stripe du site** (test/live selon la clé de site). Le
 * front n'a donc aucune clé Stripe à configurer.
 *
 * `payment_id` identifie le paiement à réclamer ensuite. Sa forme dépend du
 * type d'offre (paiement ou souscription) : traitez-le comme opaque et
 * renvoyez-le tel quel à `claimPayment`.
 */
export type PaymentResult = {
    client_secret: string;
    publishable_key: string;
    payment_id: string;
};

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

export type DashLiteClientOptions = {
    /** Clé publique du site : site_test_… (dev) ou site_live_… (production). */
    siteKey: string;
    /** Base de l'API, sans « /api/v1 ». Défaut : https://api.dash-lite.com */
    baseUrl?: string;
    /** Token Bearer d'un utilisateur déjà connecté (optionnel). */
    token?: string;
    /** Implémentation fetch personnalisée (SSR, tests). */
    fetch?: typeof fetch;
};

export class DashLiteClient {
    private readonly baseUrl: string;
    private readonly siteKey: string;
    private readonly fetchImpl: typeof fetch;
    private token: string | null;

    constructor(options: DashLiteClientOptions) {
        this.siteKey = options.siteKey;
        this.baseUrl = (options.baseUrl ?? 'https://api.dash-lite.com').replace(/\/$/, '') + '/api/v1';
        this.token = options.token ?? null;
        this.fetchImpl = options.fetch ?? fetch;
    }

    /** Attache le token Bearer aux prochains appels (après login). */
    setToken(token: string | null): void {
        this.token = token;
    }

    getToken(): string | null {
        return this.token;
    }

    // -- Authentification (CDC §5.3) ----------------------------------------

    async login(payload: { email: string; password: string }): Promise<AuthResult> {
        const result = await this.request<AuthResult>('POST', '/auth/login', payload);
        this.token = result.token;

        return result;
    }

    async logout(): Promise<void> {
        await this.request('POST', '/auth/logout');
        this.token = null;
    }

    /**
     * Change le mot de passe d'un utilisateur connecté. Toutes ses sessions
     * sont fermées ; le token retourné reconnecte l'appareil courant.
     */
    async changePassword(payload: {
        current_password: string;
        password: string;
    }): Promise<{ token: string }> {
        const result = await this.request<{ token: string }>('POST', '/auth/password', payload);
        this.token = result.token;

        return result;
    }

    async forgotPassword(email: string): Promise<void> {
        await this.request('POST', '/auth/forgot-password', { email });
    }

    async resetPassword(payload: {
        email: string;
        token: string;
        password: string;
    }): Promise<void> {
        await this.request('POST', '/auth/reset-password', payload);
    }

    async me(): Promise<Me> {
        return this.request<Me>('GET', '/me');
    }

    /** Suppression de compte (RGPD, CDC §5.3). */
    async deleteMe(): Promise<void> {
        await this.request('DELETE', '/me');
        this.token = null;
    }

    // -- Données entreprises (CDC §5.4) --------------------------------------

    /** Recherche/autocomplétion — le pays est implicite (celui du site). */
    async searchCompanies(query: string): Promise<CompanySearchResult[]> {
        return this.request<CompanySearchResult[]>(
            'GET',
            `/companies/search?q=${encodeURIComponent(query)}`,
        );
    }

    /** Fiche complète normalisée — réservée aux entitlements actifs. */
    async getCompany(registrationNumber: string): Promise<Company> {
        return this.request<Company>(
            'GET',
            `/companies/${encodeURIComponent(registrationNumber)}`,
        );
    }

    // -- Configuration, offre et paiement (CDC §5.1, §5.5) -------------------

    /**
     * Configuration publique du site : nom, pays, environnement, adresse de
     * contact et offre. **Le premier appel à faire** — il évite d'interroger
     * l'offre sur chaque page qui affiche un prix, et confirme au passage que
     * votre clé est valide.
     */
    async getSite(): Promise<SiteConfig> {
        return this.request<SiteConfig>('GET', '/site');
    }

    /**
     * Offre du site — un site n'en a qu'une, active en permanence. Vaut
     * `null` tant que le site ne l'a pas configurée. Déjà incluse dans
     * {@link getSite} : cet appel ne sert que si vous voulez la rafraîchir
     * seule.
     */
    async getOffer(): Promise<Offer | null> {
        return this.request<Offer | null>('GET', '/offer');
    }

    /**
     * Démarre le paiement de l'offre du site et retourne le `client_secret` à
     * confirmer avec la carte saisie sur **votre** page (Stripe Elements),
     * ainsi que la `publishable_key` Stripe du site — vous n'avez aucune clé
     * Stripe à configurer. Aucun parcours ne redirige vers une page de
     * paiement hébergée.
     *
     * Fonctionne à l'identique pour les trois types d'offre : paiement unique,
     * abonnement, et essai payant suivi d'un abonnement. Conservez
     * `payment_id` : il est à renvoyer à {@link claimPayment}.
     *
     * Paiement invité : sans token, l'`email` est facultatif ici (il peut être
     * fourni au claim) — le compte est créé à la réussite du paiement, avec un
     * lien de définition de mot de passe envoyé par email. Un `idempotencyKey`
     * est relayé à Stripe (CDC §5.1).
     *
     * L'**anti-fraude** est appliqué par la plateforme au moment de cet appel,
     * avant toute création chez Stripe : vous n'avez rien à intégrer. Un
     * paiement signalé lève `payment_flagged_fraud`. Si votre formulaire
     * embarque le script minFraud, joignez son `device_session_id` pour
     * affiner le scoring — c'est facultatif.
     */
    async createPayment(
        payload: {
            email?: string;
            device_session_id?: string;
            /**
             * Contexte d'achat libre : ce que le client était en train de
             * faire. Vous le retrouverez sur son droit d'accès, sans avoir à
             * tenir votre propre table de correspondance. Au plus 10 clés,
             * valeurs de 200 caractères — visible dans le back-office et dans
             * votre compte Stripe, donc pas de données sensibles.
             */
            metadata?: Record<string, string | number | boolean>;
        } = {},
        idempotencyKey?: string,
    ): Promise<PaymentResult> {
        return this.request('POST', '/payment', payload, idempotencyKey);
    }

    /**
     * Échange un paiement **abouti** contre un token, au retour de la
     * confirmation de la carte. Provisionne le compte (paiement invité) et
     * l'entitlement — idempotent, partagé avec le webhook. Le token est
     * conservé par le client.
     */
    async claimPayment(payload: { payment_id: string; email?: string }): Promise<AuthResult> {
        const result = await this.request<AuthResult>('POST', '/payment/claim', payload);
        this.token = result.token ?? this.token;

        return result;
    }

    /** L'appel qui autorise la génération du document côté front. */
    async getEntitlement(): Promise<Entitlement> {
        return this.request<Entitlement>('GET', '/entitlement');
    }

    async createBillingPortal(payload: { return_url: string }): Promise<{ portal_url: string }> {
        return this.request('POST', '/billing/portal', payload);
    }

    // -- Résiliation sans compte (CDC §7.4) -----------------------------------

    /**
     * Retrouve l'abonnement d'un client **sans qu'il ait à se connecter** : un
     * compte né d'un paiement invité n'a pas de mot de passe choisi par son
     * titulaire. Le client fournit les 4 derniers chiffres de sa carte, plus
     * son email — ou la date du prélèvement s'il ne retrouve pas l'adresse.
     *
     * Ne modifie rien : de quoi afficher un écran de confirmation. Lève
     * `subscription_not_found` si rien ne correspond,
     * `identification_ambiguous` si plusieurs personnes correspondent.
     */
    async lookupSubscription(payload: SubscriptionIdentification): Promise<SubscriptionLookup> {
        return this.request<SubscriptionLookup>('POST', '/subscription/lookup', payload);
    }

    /**
     * Résilie l'abonnement identifié par les mêmes informations. L'arrêt prend
     * effet **à la fin de la période déjà payée** : `entitlement.has_access`
     * reste `true` jusqu'à `ends_at`. Une confirmation part par email. Appeler
     * deux fois est sans effet supplémentaire.
     */
    async cancelSubscription(
        payload: SubscriptionIdentification,
    ): Promise<SubscriptionLookup & { canceled: boolean }> {
        return this.request('POST', '/subscription/cancel', payload);
    }

    // -- Newsletter (CDC §10.4) -----------------------------------------------

    /**
     * Inscrit une adresse à la newsletter du site. La réponse est identique
     * que l'adresse soit déjà inscrite ou non : un formulaire public n'a pas à
     * révéler qui figure dans votre liste.
     *
     * L'inscription peut déclencher une **séquence** de messages, si le site
     * en a configuré une.
     */
    async subscribeNewsletter(payload: {
        email: string;
        first_name?: string;
        last_name?: string;
    }): Promise<{ subscribed: boolean }> {
        return this.request('POST', '/newsletter/subscribe', payload);
    }

    /**
     * Désinscrit une adresse. Arrête tout : campagnes à venir comme séquences
     * en cours. Chaque email envoyé porte aussi un lien de désinscription en
     * un clic, qui ne demande aucune intégration de votre part.
     */
    async unsubscribeNewsletter(payload: { email: string }): Promise<{ unsubscribed: boolean }> {
        return this.request('POST', '/newsletter/unsubscribe', payload);
    }

    // -- Support (CDC §5.6) ---------------------------------------------------

    async contactSupport(payload: {
        name?: string;
        email: string;
        subject: string;
        message: string;
    }): Promise<void> {
        await this.request('POST', '/support/contact', payload);
    }

    // -------------------------------------------------------------------------

    private async request<T>(
        method: string,
        path: string,
        body?: unknown,
        idempotencyKey?: string,
    ): Promise<T> {
        const headers: Record<string, string> = {
            Accept: 'application/json',
            'X-Site-Key': this.siteKey,
        };

        if (body !== undefined) {
            headers['Content-Type'] = 'application/json';
        }

        if (this.token) {
            headers.Authorization = `Bearer ${this.token}`;
        }

        if (idempotencyKey) {
            headers['Idempotency-Key'] = idempotencyKey;
        }

        const response = await this.fetchImpl(this.baseUrl + path, {
            method,
            headers,
            body: body === undefined ? undefined : JSON.stringify(body),
        });

        if (response.status === 204) {
            return undefined as T;
        }

        const payload = (await response.json().catch(() => null)) as {
            data?: T;
            error?: { code: DashLiteErrorCode; message: string; details: Record<string, unknown> };
        } | null;

        if (!response.ok) {
            const error = payload?.error;

            throw new DashLiteApiError(
                error?.code ?? 'server_error',
                error?.message ?? 'Une erreur inattendue est survenue.',
                response.status,
                error?.details ?? {},
            );
        }

        return (payload?.data ?? payload) as T;
    }
}
